<template>
  <div>
    <paper-modal-container :is-visible="true" title="광고 신청" @close="goBack">
      <div class="apply-wrapper">
        <!-- 광고 슬롯 안내 -->
        <section class="form-section-block">
          <div class="section-header">
            <span class="section-icon">📄</span>
            <h3 class="section-title">노출 위치</h3>
          </div>
          <p class="section-desc">
            아래 두 곳에 동일한 배너가 노출됩니다. (승인 후 게재)
          </p>
          <ul class="slot-list">
            <li><strong>홈(메인) 하단</strong> — 메인 페이지 맨 아래 슬라이드 광고 영역</li>
            <li><strong>상세 페이지 하단</strong> — 라이브·아티스트·장소·영상·게시글 등 각 상세 화면 하단</li>
          </ul>
          <div class="slot-preview-grid">
            <div class="slot-preview">
              <div class="slot-preview-label">홈 하단</div>
              <div class="slot-preview-bar">메인 콘텐츠</div>
              <div class="slot-preview-ad">광고 영역</div>
              <div class="slot-preview-bar slot-preview-footer">푸터</div>
            </div>
            <div class="slot-preview">
              <div class="slot-preview-label">상세 페이지</div>
              <div class="slot-preview-bar">상세 본문</div>
              <div class="slot-preview-ad">광고 영역</div>
              <div class="slot-preview-bar">하단</div>
            </div>
          </div>
          <p class="slot-hint">배너 이미지 권장 크기 1080×360 · jpg/png/webp</p>
        </section>

        <!-- 요금 안내 -->
        <section class="form-section-block">
          <div class="section-header">
            <span class="section-icon">💎</span>
            <h3 class="section-title">월정액 요금</h3>
          </div>
          <p class="section-desc">매월 자동 결제됩니다. 언제든 해지할 수 있습니다.</p>
          <div v-if="selectedPlan" class="plan-single">
            <div class="plan-single-top">
              <span class="plan-name">{{ selectedPlan.name }}</span>
              <span class="plan-price">
                <strong>{{ formatWon(selectedPlan.monthlyAmount) }}</strong>
                <span class="plan-period">/ 월</span>
              </span>
              <span class="plan-vat-line">
                VAT {{ formatWon(selectedPlan.vat) }} · 합계 <strong>{{ formatWon(selectedPlan.totalAmount) }}</strong>/월
              </span>
            </div>
            <ul class="plan-highlights">
              <li v-for="(h, i) in selectedPlan.highlights" :key="i">{{ h }}</li>
            </ul>
            <p class="plan-policy-hint">
              결제 전 취소·환불 규정은 다음 단계(결제 안내)에서 확인·동의합니다.
            </p>
          </div>
        </section>

        <!-- 광고주 정보 -->
        <section class="form-section-block">
          <div class="section-header">
            <span class="section-icon">🏢</span>
            <h3 class="section-title">광고주 정보</h3>
          </div>
          <p class="section-desc">청구서(Invoice) 자동 발행 및 세금 처리에 사용됩니다.</p>
          <div class="field-grid">
            <div class="field">
              <label>회사명(상호) <span class="required">*</span></label>
              <input v-model="form.advertiser.companyName" type="text" maxlength="60" />
            </div>
            <div class="field">
              <label>대표자명 <span class="required">*</span></label>
              <input v-model="form.advertiser.representativeName" type="text" maxlength="30" />
            </div>
            <div class="field">
              <label>사업자등록번호 <span class="required">*</span></label>
              <input v-model="form.advertiser.registrationNumber" type="text" maxlength="12"
                placeholder="000-00-00000" />
            </div>
            <div class="field">
              <label>업태</label>
              <input v-model="form.advertiser.businessType" type="text" maxlength="40" />
            </div>
            <div class="field">
              <label>종목</label>
              <input v-model="form.advertiser.businessItem" type="text" maxlength="40" />
            </div>
            <div class="field field-full">
              <label>사업장 주소</label>
              <input v-model="form.advertiser.address" type="text" maxlength="120" />
            </div>
            <div class="field">
              <label>담당자명 <span class="required">*</span></label>
              <input v-model="form.advertiser.contactName" type="text" maxlength="30" />
            </div>
            <div class="field">
              <label>담당자 이메일 <span class="required">*</span></label>
              <input v-model="form.advertiser.contactEmail" type="email" maxlength="80" />
            </div>
            <div class="field">
              <label>담당자 연락처 <span class="required">*</span></label>
              <input v-model="form.advertiser.contactPhone" type="tel" maxlength="20" placeholder="010-0000-0000" />
            </div>
          </div>
        </section>

        <!-- 광고 소재 -->
        <section class="form-section-block">
          <div class="section-header">
            <span class="section-icon">🎨</span>
            <h3 class="section-title">광고 상세정보</h3>
          </div>
          <p class="section-desc">광고 이미지는 결제 후 관리자 심사를 거쳐 노출됩니다.</p>
          <div class="field-grid">
            <div class="field field-full">
              <label>광고 제목 <span class="required">*</span></label>
              <input v-model="form.material.title" type="text" maxlength="60" placeholder="예) 2026 봄 페스티벌 얼리버드 오픈" />
            </div>
            <div class="field field-full">
              <label>배너 이미지 <span class="required">*</span></label>
              <div class="image-uploader">
                <div v-if="imagePreview" class="image-preview-wrapper">
                  <img :src="imagePreview" alt="광고 이미지 미리보기" class="image-preview" />
                  <button type="button" class="image-clear-btn" @click="clearImage">이미지 변경</button>
                </div>
                <div v-else class="image-upload-empty">
                  <p class="upload-hint">권장 크기 1080×360 · 5MB 이하 · jpg/png/webp</p>
                  <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/webp" class="file-input"
                    @change="onImageChange" />
                  <button type="button" class="upload-btn" @click="triggerFile" :disabled="uploadingImage">
                    {{ uploadingImage ? `업로드 중 ${uploadProgress}%` : "📁 이미지 선택" }}
                  </button>
                </div>
              </div>
            </div>
            <div class="field field-full">
              <label>클릭 시 이동 URL <span class="required">*</span></label>
              <input v-model="form.material.link" type="url" maxlength="500" placeholder="https://..." />
            </div>
            <div class="field field-full">
              <label>광고 설명 (선택, 심사용)</label>
              <textarea v-model="form.material.description" rows="3" maxlength="500"
                placeholder="광고 내용·맥락을 간단히 적어주세요." />
            </div>
          </div>
        </section>

        <!-- 액션 -->
        <div class="action-row">
          <button type="button" class="secondary-btn" :disabled="saving" @click="onSaveDraft">
            {{ saving ? "저장 중..." : "임시저장" }}
          </button>
          <button type="button" class="primary-btn" :disabled="saving || uploadingImage" @click="onProceedCheckout">
            결제 단계로 →
          </button>
        </div>
      </div>
    </paper-modal-container>
  </div>
</template>

<script>
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";
import { useUserStore } from "@/store/user";
import {
  AD_PLAN_MAP,
  DEFAULT_AD_PLAN_ID,
  buildPlanSnapshot,
  formatWon,
  createAdCampaignDraft,
  updateAdCampaignDraft,
  uploadAdMaterialImage,
  getAdCampaign,
  createLocalPreviewCampaignId,
  getLocalAdDraft,
  isLocalPreviewCampaignId,
  saveLocalAdDraft,
} from "@/utils/adApplication";

const REG_NUMBER_REGEX = /^\d{3}-?\d{2}-?\d{5}$/;
const URL_REGEX = /^https?:\/\/.+/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

function initForm() {
  return {
    plan: DEFAULT_AD_PLAN_ID,
    advertiser: {
      companyName: "",
      representativeName: "",
      registrationNumber: "",
      businessType: "",
      businessItem: "",
      address: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
    },
    material: {
      title: "",
      imageUrl: "",
      imageFileName: "",
      link: "",
      description: "",
    },
  };
}

export default {
  name: "AdApply",
  components: { PaperModalContainer },
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  data() {
    return {
      form: initForm(),
      campaignId: "",
      imagePreview: "",
      uploadingImage: false,
      uploadProgress: 0,
      saving: false,
      loaded: false,
    };
  },
  computed: {
    selectedPlan() {
      return AD_PLAN_MAP[this.form.plan] || null;
    },
  },
  async mounted() {
    const id = String(this.$route.params.id || "").trim();
    if (id) {
      this.campaignId = id;
      await this.loadDraft();
    }
    this.loaded = true;
  },
  methods: {
    formatWon,
    triggerFile() {
      this.$refs.fileInput?.click();
    },
    clearImage() {
      this.imagePreview = "";
      this.form.material.imageUrl = "";
      this.form.material.imageFileName = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    applyDraftToForm(campaign) {
      if (!campaign) return;
      this.form.plan = campaign.plan || DEFAULT_AD_PLAN_ID;
      this.form.advertiser = { ...this.form.advertiser, ...(campaign.advertiser || {}) };
      this.form.material = { ...this.form.material, ...(campaign.material || {}) };
      if (campaign.material?.imageUrl) {
        this.imagePreview = campaign.material.imageUrl;
      }
    },
    async loadDraft() {
      if (isLocalPreviewCampaignId(this.campaignId)) {
        const local = getLocalAdDraft(this.campaignId);
        if (local) this.applyDraftToForm(local);
        return;
      }
      try {
        const campaign = await getAdCampaign(this.campaignId);
        if (!campaign) return;
        if (campaign.status && campaign.status !== "draft") {
          this.$alert?.("이미 결제가 진행된 광고는 수정할 수 없습니다.");
          this.$router.replace(`/my-ads/${this.campaignId}`);
          return;
        }
        this.applyDraftToForm(campaign);
      } catch (e) {
        console.error("draft 로드 실패", e);
        const local = getLocalAdDraft(this.campaignId);
        if (local) {
          this.applyDraftToForm(local);
          return;
        }
        this.$alert?.("임시 저장된 신청서를 불러오지 못했습니다.");
      }
    },
    saveLocalPreviewDraft() {
      const id = this.campaignId || createLocalPreviewCampaignId();
      this.campaignId = id;
      saveLocalAdDraft(id, this.buildPayload());
      if (!String(this.$route.params.id || "").trim()) {
        this.$router.replace(`/ads/apply/${id}`);
      }
      return id;
    },
    async ensureCampaign() {
      if (isLocalPreviewCampaignId(this.campaignId)) {
        return this.saveLocalPreviewDraft();
      }
      try {
        if (this.campaignId) {
          await updateAdCampaignDraft(this.campaignId, this.buildPayload());
          return this.campaignId;
        }
        const created = await createAdCampaignDraft(this.buildPayload());
        const id = created?.id || created?.campaignId;
        if (!id) throw new Error("캠페인 ID를 반환받지 못했습니다.");
        this.campaignId = id;
        this.$router.replace(`/ads/apply/${id}`);
        return id;
      } catch (e) {
        console.warn("서버 저장 실패 — 로컬 미리보기로 진행", e);
        return this.saveLocalPreviewDraft();
      }
    },
    validate({ requireMaterial = true } = {}) {
      const { advertiser, material, plan } = this.form;
      if (!AD_PLAN_MAP[plan]) return "요금 정보를 확인할 수 없습니다.";
      if (!advertiser.companyName.trim()) return "회사명(상호)을 입력해주세요.";
      if (!advertiser.representativeName.trim()) return "대표자명을 입력해주세요.";
      if (!REG_NUMBER_REGEX.test(advertiser.registrationNumber.trim()))
        return "사업자등록번호 형식이 올바르지 않습니다.";
      if (!advertiser.contactName.trim()) return "담당자명을 입력해주세요.";
      if (!EMAIL_REGEX.test(advertiser.contactEmail.trim()))
        return "담당자 이메일 형식이 올바르지 않습니다.";
      if (!advertiser.contactPhone.trim()) return "담당자 연락처를 입력해주세요.";

      if (requireMaterial) {
        if (!material.title.trim()) return "광고 제목을 입력해주세요.";
        if (!material.imageUrl) return "광고 배너 이미지를 업로드해주세요.";
        if (!URL_REGEX.test(material.link.trim()))
          return "클릭 시 이동 URL은 http:// 또는 https:// 로 시작해야 합니다.";
      }
      return null;
    },
    buildPayload() {
      return {
        plan: this.form.plan,
        planSnapshot: buildPlanSnapshot(this.form.plan),
        advertiser: { ...this.form.advertiser },
        material: { ...this.form.material },
      };
    },
    async onImageChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      if (file.size > MAX_IMAGE_BYTES) {
        this.$alert?.("이미지는 5MB 이하만 업로드할 수 있습니다.");
        event.target.value = "";
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      this.imagePreview = previewUrl;
      try {
        this.uploadingImage = true;
        this.uploadProgress = 0;
        const validationError = this.validate({ requireMaterial: false });
        if (validationError) {
          this.$alert?.(validationError);
          this.uploadingImage = false;
          this.imagePreview = "";
          event.target.value = "";
          return;
        }
        const id = await this.ensureCampaign();
        if (!isLocalPreviewCampaignId(id)) {
          const result = await uploadAdMaterialImage(id, file, (p) => {
            this.uploadProgress = p;
          });
          this.form.material.imageUrl = result?.url || result?.downloadURL || "";
          this.form.material.imageFileName = result?.fileName || file.name;
        } else {
          this.form.material.imageUrl = previewUrl;
          this.form.material.imageFileName = file.name;
          saveLocalAdDraft(id, this.buildPayload());
        }
      } catch (e) {
        console.error("이미지 업로드 실패", e);
        const id = this.saveLocalPreviewDraft();
        this.form.material.imageUrl = previewUrl;
        this.form.material.imageFileName = file.name;
        saveLocalAdDraft(id, this.buildPayload());
        console.warn("이미지는 브라우저 미리보기로만 유지됩니다 (서버 미저장).");
      } finally {
        this.uploadingImage = false;
        this.uploadProgress = 0;
        if (event?.target) event.target.value = "";
      }
    },
    async onSaveDraft() {
      const err = this.validate({ requireMaterial: false });
      if (err) {
        this.$alert?.(err);
        return;
      }
      try {
        this.saving = true;
        const id = await this.ensureCampaign();
        const msg = isLocalPreviewCampaignId(id)
          ? "브라우저에만 임시 저장되었습니다. (서버 미연동)"
          : "임시저장되었습니다.";
        this.$alert?.(msg);
        this.$router.push("/my-ads");
      } catch (e) {
        console.error("임시저장 실패", e);
        this.$alert?.(e?.response?.data?.message || e?.message || "임시저장에 실패했습니다.");
      } finally {
        this.saving = false;
      }
    },
    async onProceedCheckout() {
      const err = this.validate({ requireMaterial: true });
      if (err) {
        this.$alert?.(err);
        return;
      }
      try {
        this.saving = true;
        const id = await this.ensureCampaign();
        if (isLocalPreviewCampaignId(id)) {
          console.warn("결제 화면 미리보기 모드 (서버 저장 없음)");
        }
        this.$router.push(`/ads/apply/${id}/checkout`);
      } catch (e) {
        console.error("결제 단계 진입 실패", e);
        const id = this.saveLocalPreviewDraft();
        this.$router.push(`/ads/apply/${id}/checkout`);
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push("/my-ads");
    },
  },
};
</script>

<style scoped>
.apply-wrapper {
  padding: 24px 0 80px;
  max-width: 900px;
  margin: 0 auto;
  text-align: left;
}

.form-section-block {
  padding: 28px 0;
  border-bottom: 1px solid #e9ecef;
}

.form-section-block:last-of-type {
  border-bottom: none;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.section-icon {
  font-size: 18px;
}

.section-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 17px;
  color: #2d3748;
  margin: 0;
}

.section-desc {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #6c757d;
  margin: 0 0 16px;
  line-height: 1.6;
}

/* 노출 위치 */
.slot-list {
  margin: 0 0 16px;
  padding-left: 18px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #495057;
  line-height: 1.8;
}

.slot-list strong {
  color: #2d3748;
}

.slot-preview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 10px;
}

.slot-preview-label {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  color: #495057;
  margin-bottom: 8px;
  text-align: center;
}

.slot-hint {
  margin: 0;
  font-size: 12px;
  color: #868e96;
  text-align: center;
}

/* 슬롯 미리보기 */
.slot-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
}

.slot-preview-footer {
  font-size: 11px;
}

.slot-preview-bar {
  background: #fff;
  padding: 16px;
  border-radius: 6px;
  color: #adb5bd;
  font-size: 12px;
  text-align: center;
}

.slot-preview-ad {
  background: linear-gradient(135deg, #66bdff 0%, #4da3ff 100%);
  color: #fff;
  padding: 28px;
  border-radius: 6px;
  font-family: "NanumSquareRoundEB", sans-serif;
  text-align: center;
}

/* 요금 안내 */
.plan-single {
  background: #f8f9fa;
  border: 2px solid #66bdff;
  border-radius: 12px;
  padding: 20px;
}

.plan-single-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.plan-name {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 16px;
  color: #2d3748;
}

.plan-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.plan-price strong {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 22px;
  color: #1c7ed6;
}

.plan-period {
  font-size: 12px;
  color: #868e96;
}

.plan-vat-line {
  font-size: 13px;
  color: #495057;
}

.plan-vat-line strong {
  color: #1c7ed6;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.plan-highlights {
  padding-left: 18px;
  margin: 0;
  color: #6c757d;
  font-size: 13px;
  line-height: 1.7;
}

.plan-policy-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: #868e96;
  line-height: 1.5;
}

/* 필드 */
.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-full {
  grid-column: 1 / -1;
}

.field label {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #495057;
}

.field .required {
  color: #ef4444;
}

.field input,
.field textarea {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: #212529;
  background: #fff;
}

.field input:focus,
.field textarea:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.15);
}

/* 이미지 업로더 */
.image-uploader {
  border: 1px dashed #dee2e6;
  border-radius: 8px;
  padding: 16px;
  background: #fafbfc;
  text-align: center;
}

.image-preview-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.image-preview {
  max-width: 100%;
  max-height: 240px;
  border-radius: 6px;
  object-fit: contain;
}

.image-clear-btn {
  background: transparent;
  border: 1px solid #ced4da;
  color: #495057;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}

.image-upload-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-hint {
  font-size: 12px;
  color: #868e96;
  margin: 0;
}

.file-input {
  display: none;
}

.upload-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: #66bdff;
  color: #fff;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  cursor: pointer;
}

.upload-btn:disabled {
  opacity: 0.6;
  cursor: progress;
}

/* 액션 */
.action-row {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 32px;
}

.secondary-btn {
  padding: 12px 22px;
  background: #f1f3f5;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.secondary-btn:hover {
  background: #e9ecef;
}

.primary-btn {
  padding: 12px 22px;
  background: #66bdff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.primary-btn:hover {
  background: #4da3ff;
}

.primary-btn:disabled,
.secondary-btn:disabled {
  opacity: 0.6;
  cursor: progress;
}

@media (max-width: 720px) {
  .slot-preview-grid {
    grid-template-columns: 1fr;
  }

  .field-grid {
    grid-template-columns: 1fr;
  }

  .action-row {
    flex-direction: column;
  }

  .secondary-btn,
  .primary-btn {
    width: 100%;
  }
}
</style>
